import type { ItemRef, Snapshot } from '../shared/contract';
import type { SlipPrinter } from './slip';

/**
 * ERSTMALS — the one-time asides, after the Lehrnacht is over.
 *
 * Die Lehrnacht teaches five things in eight minutes. It cannot teach twelve, and a
 * tutorial that tries becomes the thing the player is playing instead of the game. So
 * everything else is taught **the first time it is in front of you**, once, ever, in the
 * same printed-slip voice, and then never again for the life of the profile.
 *
 * The rules that keep this from becoming a nag:
 *
 * - **≤ 10 of them, full stop.** Nine are written below and the budget is the design.
 *   An eleventh would mean cutting one, and that argument is worth having.
 * - **Once per profile, not per run.** Stored in `Progress.erstmals`. The player learned
 *   what a crooked "?" means on the night they first saw one; reminding them on night
 *   four is calling them stupid.
 * - **Fired by the world, not by a script.** Each trigger below is a question about the
 *   current snapshot. Nothing sequences them, nothing waits for them, and a player who
 *   never meets Der Rausschmeißer never reads his line.
 * - **≤ 2 lines each**, same ≤ 8-word Croupier voice as the Lehrnacht (`slip.ts`
 *   enforces the word count and will refuse the line loudly if it is broken).
 *
 * They are deliberately *not* dismissible-with-a-button: a slip curls away on its own,
 * and any input the player makes carries on working underneath it. Nothing here ever
 * blocks a hand.
 */

export interface Erstmals {
  id: string;
  /** is this thing in front of the player right now? */
  when: (s: Snapshot) => boolean;
  /** ≤ 2 lines, ≤ 8 words each */
  lines: readonly string[];
  /** the House taking something: red slip */
  cold?: boolean;
}

const hasItem = (items: readonly ItemRef[], id: string): boolean =>
  items.some((i) => i.id === id);

/**
 * The nine. Ordered by roughly when a player meets them, which is also the order they
 * are checked in — at most one fires per frame, so two things arriving together are read
 * one after the other rather than stacked.
 */
export const ERSTMALS: readonly Erstmals[] = Object.freeze([
  {
    id: 'zinken:verdacht',
    when: (s) => s.room.some((c) => c.suspect),
    lines: [
      'Mit einem Automaten stimmt etwas nicht.',
      'Das schiefe Fragezeichen. Spielen Sie ihn.',
    ],
  },
  {
    id: 'zinken:pruefen',
    when: (s) => s.run.items.some((i) => i.kind === 'zinken'),
    lines: [
      'F prüft einen Automaten auf Zinken.',
      'Kostet GLÜCK. Spart manchmal alles.',
    ],
  },
  {
    id: 'struktur',
    when: (s) => (s.cabinet?.limit01 ?? 0) > 0.7 && !(s.cabinet?.broken ?? false),
    lines: [
      'Die Scheibe hält das nicht.',
      'Über der Grenze zahlt alles auf einmal.',
    ],
    cold: true,
  },
  {
    id: 'fluch',
    when: (s) => s.run.beutel.some((t) => t.tags.includes('Fluch')),
    lines: [
      'Eine verfluchte Marke. Sie zahlt nichts.',
      'Aber sie verliert. Und Verlieren macht GLÜCK.',
    ],
  },
  {
    id: 'wunder',
    when: (s) => s.run.wunder !== null,
    lines: [
      'Ein Automat, den niemand bestellt hat.',
      'Er nimmt Tickets. Keine Münzen.',
    ],
  },
  {
    id: 'saalchef',
    when: (s) => s.run.tickets >= s.run.ticketQuota && s.run.ticketQuota > 0,
    lines: [
      'Die Tür zum Saalchef geht auf.',
      'Er spielt Flipper. Sie leider auch.',
    ],
  },
  {
    id: 'rausschmeisser',
    when: (s) => s.events.some((e) => e.t === 'rausschmeisser' && e.phase === 'arrive'),
    lines: [
      'Er lehnt sich an Ihren Automaten.',
      'Sein Rütteln zählt auf Ihren TILT.',
    ],
    cold: true,
  },
  {
    id: 'brecheisen',
    when: (s) => hasItem(s.run.items, 'item:brecheisen'),
    lines: [
      'Ein Brecheisen. B am richtigen Automaten.',
      'Was das Haus einbaut, können Sie mitnehmen.',
    ],
  },
  /**
   * The one aside that points at the reference layer, and the only place in the entire
   * game that mentions it. It waits until the player is standing at a cabinet with a
   * non-empty tag table, because a Hilfekarte offered while looking at the aisle is a
   * Hilfekarte about nothing.
   */
  {
    id: 'hilfekarte',
    when: (s) => (s.cabinet?.helpCard.length ?? 0) > 0,
    lines: [
      'Jeder Automat hat eine Hilfekarte.',
      'H halten. Da steht, was er kann.',
    ],
  },
]);

export interface ErstmalsOpts {
  slips: SlipPrinter;
  /** ids already seen by this profile */
  seen: readonly string[];
  /** called when a new one fires, so the shell can persist it */
  onSeen: (id: string) => void;
}

/** Watches the snapshot feed and prints at most one aside per frame. */
export class ErstmalsWatcher {
  private readonly seen: Set<string>;
  private readonly o: ErstmalsOpts;
  /** a short quiet period after each one, so two firsts never read as a lecture */
  private cooldown = 0;

  constructor(o: ErstmalsOpts) {
    this.o = o;
    this.seen = new Set(o.seen);
  }

  observe(s: Snapshot): void {
    if (this.cooldown > 0) {
      this.cooldown--;
      return;
    }
    // never talk over him, and never over a spectacle
    if (this.o.slips.busy) return;
    if (s.run.phase === 'receipt' || s.run.phase === 'over') return;

    for (const e of ERSTMALS) {
      if (this.seen.has(e.id)) continue;
      if (!e.when(s)) continue;
      this.seen.add(e.id);
      this.o.onSeen(e.id);
      for (const line of e.lines) this.o.slips.say({ text: line, cold: e.cold });
      this.cooldown = 240; // ~2 s of frames at 120 Hz; a pause, not a queue
      return;
    }
  }

  /** For the E2E and for the Vitrine: what this profile has been told. */
  get told(): string[] {
    return [...this.seen];
  }
}
